/**
 * Carrier MCP — Projects Tools (Stripe Projects CLI Pattern)
 *
 * Implements the Stripe Projects CLI paradigm for Carrier:
 *   - Service catalog browsing (carrier ecosystem services)
 *   - Credential vault management (API token rotation, env sync)
 *   - Billing management via natural language (upgrade/downgrade)
 *   - LLM context generation for AI agent workflows
 *   - Multi-environment management (dev/staging/prod)
 *
 * These tools enable AI agents to:
 *   1. Discover and provision Carrier services
 *   2. Manage credentials securely
 *   3. Handle billing operations programmatically
 *   4. Generate context for downstream AI workflows
 *
 * Inspired by: `stripe projects init`, `stripe projects add`, `stripe projects catalog`
 */

import type { Env, CarrierProps, ToolScope } from "./types.js";
import type { Tier } from "./billing.js";
import { TIER_CREDIT_ALLOTMENTS, SCOPE_CREDIT_COSTS } from "./credits.js";
import { requireUserStore } from "./binding-unavailable.js";

// ---------------------------------------------------------------------------
// Service Catalog
// ---------------------------------------------------------------------------

export interface CarrierService {
  id: string;
  name: string;
  category: string;
  description: string;
  tier_required: Tier;
  scopes_required: string[];
  endpoints: string[];
  docs_url: string;
}

const CARRIER_SERVICE_CATALOG: CarrierService[] = [
  {
    id: "mcp",
    name: "Carrier MCP",
    category: "connectivity",
    description: "Model Context Protocol server — 113 natural-language tools for MVNO/eSIM fleet management",
    tier_required: "free",
    scopes_required: ["read"],
    endpoints: ["https://mcp.carrier.llc/mcp"],
    docs_url: "https://mcp.carrier.llc/docs",
  },
  {
    id: "api",
    name: "Carrier REST API",
    category: "connectivity",
    description: "RESTful HTTP API for programmatic OCS access — same tool surface as MCP over standard REST",
    tier_required: "pro",
    scopes_required: ["read", "write"],
    endpoints: ["https://api.carrier.llc/v1"],
    docs_url: "https://api.carrier.llc/docs",
  },
  {
    id: "intelligence",
    name: "Carrier Intelligence",
    category: "analytics",
    description: "AI-powered fleet analytics — churn prediction, usage anomalies, coverage optimization, revenue intelligence",
    tier_required: "pro",
    scopes_required: ["read"],
    endpoints: ["https://mcp.carrier.llc/mcp"],
    docs_url: "https://mcp.carrier.llc/docs#intelligence",
  },
  {
    id: "connect",
    name: "Carrier Connect",
    category: "marketplace",
    description: "MNO/MVNO network marketplace — browse operators, coverage maps, steering list management",
    tier_required: "pro",
    scopes_required: ["read", "write"],
    endpoints: ["https://app.carrier.llc/connect"],
    docs_url: "https://mcp.carrier.llc/docs#connect",
  },
  {
    id: "atlas",
    name: "Carrier Atlas",
    category: "coverage",
    description: "Global coverage intelligence — real-time network quality, latency maps, operator benchmarks",
    tier_required: "enterprise",
    scopes_required: ["read"],
    endpoints: ["https://atlas.carrier.llc/api/v1"],
    docs_url: "https://atlas.carrier.llc/docs",
  },
  {
    id: "billing",
    name: "Carrier Billing",
    category: "billing",
    description: "Operator billing engine — CDR ingestion, invoice generation, Stripe Connect payouts",
    tier_required: "pro",
    scopes_required: ["read", "write"],
    endpoints: ["https://api.carrier.llc/v1/billing"],
    docs_url: "https://api.carrier.llc/docs#billing",
  },
  {
    id: "webhooks",
    name: "Carrier Webhooks",
    category: "integration",
    description: "Real-time event delivery — OCS events, billing alerts, threshold notifications via HTTP webhooks",
    tier_required: "pro",
    scopes_required: ["read"],
    endpoints: ["https://api.carrier.llc/v1/webhooks"],
    docs_url: "https://api.carrier.llc/docs#webhooks",
  },
  {
    id: "console",
    name: "Carrier Console",
    category: "dashboard",
    description: "Visual operator dashboard — fleet management, analytics, billing, onboarding for non-technical operators",
    tier_required: "free",
    scopes_required: ["read"],
    endpoints: ["https://app.carrier.llc"],
    docs_url: "https://carrier.llc/docs",
  },
];

// ---------------------------------------------------------------------------
// Tool Definitions
// ---------------------------------------------------------------------------

export interface ProjectsToolDef {
  name: string;
  description: string;
  scope: ToolScope;
  inputSchema: Record<string, unknown>;
  handler: (
    env: Env,
    props: CarrierProps,
    args: Record<string, unknown>,
  ) => Promise<unknown>;
}

export const PROJECTS_TOOLS: ProjectsToolDef[] = [
  // -------------------------------------------------------------------------
  // service_catalog — Browse available Carrier services
  // -------------------------------------------------------------------------
  {
    name: "service_catalog",
    description:
      "Browse the Carrier service catalog — discover available services, their " +
      "requirements, endpoints, and documentation. Filter by category or tier. " +
      "Similar to `stripe projects catalog`.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {
        category: {
          type: "string",
          enum: ["connectivity", "analytics", "marketplace", "coverage", "billing", "integration", "dashboard"],
          description: "Filter services by category",
        },
        tier: {
          type: "string",
          enum: ["free", "pro", "enterprise"],
          description: "Filter services accessible at this tier level",
        },
      },
      required: [],
    },
    handler: async (_env, props, args) => {
      let services = [...CARRIER_SERVICE_CATALOG];

      if (typeof args.category === "string") {
        services = services.filter((s) => s.category === args.category);
      }

      if (typeof args.tier === "string") {
        const tierOrder: Record<string, number> = { free: 0, pro: 1, enterprise: 2 };
        const maxTier = tierOrder[args.tier] ?? 0;
        services = services.filter(
          (s) => (tierOrder[s.tier_required] ?? 0) <= maxTier,
        );
      }

      // Mark which services are accessible with current tier
      const currentTierOrder: Record<string, number> = { free: 0, pro: 1, enterprise: 2 };
      const userTierLevel = currentTierOrder[props.tier] ?? 0;

      return {
        services: services.map((s) => ({
          ...s,
          accessible: (currentTierOrder[s.tier_required] ?? 0) <= userTierLevel,
        })),
        total: services.length,
        current_tier: props.tier,
        upgrade_url: "https://mcp.carrier.llc/upgrade",
      };
    },
  },

  // -------------------------------------------------------------------------
  // credential_status — Check credential health and rotation status
  // -------------------------------------------------------------------------
  {
    name: "credential_status",
    description:
      "Check the health and status of your Carrier credentials — API token validity, " +
      "encryption status, last rotation date, and expiry warnings. " +
      "Similar to `stripe projects env --pull` status check.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async (env, props) => {
      const sub = props.sub;
      const orgId = props.org_id;

      // stdio mode has no KV — token comes from env vars.
      if (!env.CARRIER_USERS) {
        return {
          credentials: {
            esimvault_token: {
              present: true,
              encrypted: false,
              last_updated: null,
              age_days: null,
              rotation_recommended: false,
              source: "env-var (stdio)",
            },
            oauth: { active: false, method: props.auth_method ?? "stdio" },
          },
          environment: {
            org_id: orgId ?? null,
            reseller_id: props.reseller_id,
            reseller_name: props.reseller_name,
          },
          recommendations: ["stdio mode — token managed via ESIMVAULT_API_TOKEN env var, no rotation tracking"],
        };
      }

      // KV key shape mirrors auth.ts: org:<orgId> or user:clerk_<userId>.
      const primaryKey = orgId ? `org:${orgId}` : `user:clerk_${sub}`;
      const legacyKey = orgId ? null : `user:${sub}`;

      let record = await env.CARRIER_USERS.get(primaryKey, "json").catch(() => null) as Record<string, unknown> | null;
      if (!record && legacyKey) {
        record = await env.CARRIER_USERS.get(legacyKey, "json").catch(() => null) as Record<string, unknown> | null;
      }

      const hasToken = !!(record && "esimvault_token_enc" in record && record.esimvault_token_enc);

      // Check OAuth grant status — guard OAUTH_KV (also absent in stdio)
      const oauthKey = `oauth:grant:${sub}`;
      const oauthGrant = env.OAUTH_KV
        ? await env.OAUTH_KV.get(oauthKey, "json").catch(() => null) as Record<string, unknown> | null
        : null;

      const updatedAt = record && "updated_at" in record
        ? record.updated_at as string
        : null;

      // Calculate token age
      let tokenAgeDays: number | null = null;
      if (updatedAt) {
        const updated = new Date(updatedAt);
        tokenAgeDays = Math.floor((Date.now() - updated.getTime()) / (1000 * 60 * 60 * 24));
      }

      return {
        credentials: {
          esimvault_token: {
            present: hasToken,
            encrypted: hasToken, // All tokens are AES-256-GCM encrypted
            last_updated: updatedAt,
            age_days: tokenAgeDays,
            rotation_recommended: tokenAgeDays !== null && tokenAgeDays > 90,
          },
          oauth: {
            active: !!oauthGrant,
            method: props.auth_method ?? "oauth",
          },
        },
        environment: {
          org_id: orgId ?? null,
          reseller_id: props.reseller_id,
          reseller_name: props.reseller_name,
        },
        recommendations: generateCredentialRecommendations(hasToken, tokenAgeDays),
      };
    },
  },

  // -------------------------------------------------------------------------
  // rotate_credentials — Initiate credential rotation
  // -------------------------------------------------------------------------
  {
    name: "rotate_credentials",
    description:
      "Initiate rotation of your eSIMVault API credentials. Generates a new " +
      "encrypted token and invalidates the old one. Requires write scope. " +
      "Similar to `stripe projects rotate <service>`.",
    scope: "write",
    inputSchema: {
      type: "object",
      properties: {
        new_token: {
          type: "string",
          description: "New eSIMVault API token to encrypt and store. Get from eSIMVault dashboard.",
        },
        confirm: {
          type: "boolean",
          description: "Confirm rotation — this will replace the current token immediately",
        },
      },
      required: ["new_token", "confirm"],
    },
    handler: async (env, props, args) => {
      // Checked before anything else, and before the confirm/format replies
      // below, because a "cancelled" or "invalid token" answer implies the tool
      // would otherwise have worked. It would not: the vault this writes to is
      // the hosted one.
      requireUserStore(
        env,
        "rotate_credentials",
        "the encrypted token vault this writes to",
      );

      if (!args.confirm) {
        return {
          status: "cancelled",
          message: "Rotation cancelled — set confirm: true to proceed",
        };
      }

      const newToken = args.new_token as string;
      if (!newToken || newToken.length < 10) {
        return {
          error: "Invalid token — must be at least 10 characters",
        };
      }

      // Encrypt the new token
      const encryptionKey = (env as Env & { CARRIER_TOKEN_ENCRYPTION_KEY?: string })
        .CARRIER_TOKEN_ENCRYPTION_KEY;
      if (!encryptionKey) {
        // Thrown, not returned. Returning it produced a SUCCESS-shaped result
        // with an `error` string inside — the tool reported "ok" to the client
        // and only a human reading the JSON would notice. "Contact support" was
        // also not an action: the key is a deployment secret of the hosted
        // service, and its absence means this build is not the hosted service.
        throw new Error(
          "rotate_credentials cannot run here: CARRIER_TOKEN_ENCRYPTION_KEY is a " +
            "secret of the hosted Carrier service and is not present in a local " +
            "install, so there is no way to encrypt a token for the vault. " +
            "Rotate from the console at https://app.carrier.llc, or connect to the " +
            "hosted MCP at https://mcp.carrier.llc/mcp. In local stdio mode the OCS " +
            "token comes from CARRIER_OCS_API_TOKEN in your own environment — change " +
            "it there and restart, no rotation call needed.",
        );
      }

      const encrypted = await encryptToken(newToken, encryptionKey);
      const orgId = props.org_id;
      const recordKey = orgId ? `org:${orgId}` : `user:${props.sub}`;

      // Update the record
      const existing = await env.CARRIER_USERS.get(recordKey, "json").catch(() => null) as Record<string, unknown> | null;
      if (!existing) {
        return { error: "User record not found" };
      }

      const updated = {
        ...existing,
        esimvault_token_enc: encrypted,
        updated_at: new Date().toISOString(),
      };

      await env.CARRIER_USERS.put(recordKey, JSON.stringify(updated));

      return {
        status: "rotated",
        message: "Credentials rotated successfully. New token is active immediately.",
        encrypted: true,
        rotated_at: new Date().toISOString(),
        next_rotation_recommended: new Date(
          Date.now() + 90 * 24 * 60 * 60 * 1000,
        ).toISOString(),
      };
    },
  },

  // -------------------------------------------------------------------------
  // llm_context — Generate LLM context for AI agent workflows
  // -------------------------------------------------------------------------
  {
    name: "llm_context",
    description:
      "Generate a comprehensive LLM context document describing your Carrier " +
      "environment, available tools, current tier, usage patterns, and best " +
      "practices. Designed for AI agents that need to understand your setup. " +
      "Similar to `stripe projects llm-context`.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {
        format: {
          type: "string",
          enum: ["markdown", "json", "yaml"],
          description: "Output format for the context document (default: markdown)",
        },
        include_examples: {
          type: "boolean",
          description: "Include example tool invocations (default: true)",
        },
      },
      required: [],
    },
    handler: async (env, props, args) => {
      void env; // used for future expansion
      const format = (args.format as string) ?? "markdown";
      const includeExamples = args.include_examples !== false;
      const tier = props.tier as Tier;

      // Build context
      const context = {
        project: {
          name: "Carrier MCP",
          description: "Programmable connectivity API control plane for MVNO/eSIM fleet management",
          version: "2.0",
          transport: "StreamableHTTP",
          endpoint: "https://mcp.carrier.llc/mcp",
        },
        environment: {
          tier,
          scopes: props.scope,
          reseller_id: props.reseller_id,
          reseller_name: props.reseller_name,
          org_id: props.org_id ?? null,
          auth_method: props.auth_method ?? "oauth",
        },
        capabilities: {
          total_tools: 113, // full tool count
          read_tools: 35,
          write_tools: 20,
          admin_tools: 10,
          intelligence_tools: 8,
          pricing_tools: 5,
          projects_tools: 5,
          prompts: 5,
        },
        billing: {
          credits_monthly: TIER_CREDIT_ALLOTMENTS[tier],
          credit_costs: SCOPE_CREDIT_COSTS,
          overages_available: tier !== "free",
          volume_discounts_available: tier !== "free",
        },
        available_services: CARRIER_SERVICE_CATALOG.filter((s) => {
          const tierOrder: Record<string, number> = { free: 0, pro: 1, enterprise: 2 };
          return (tierOrder[s.tier_required] ?? 0) <= (tierOrder[tier] ?? 0);
        }).map((s) => ({ id: s.id, name: s.name, category: s.category })),
        best_practices: [
          "Always check credit_balance before batch operations",
          "Use intelligence tools for fleet diagnostics before manual investigation",
          "Configure billing thresholds to prevent bill shock on overage-enabled plans",
          "Rotate credentials every 90 days for security",
          "Use carrier_ask when unsure which tool to call",
          "Prefer read-scope tools (1 credit) over write-scope (2 credits) when possible",
        ],
        examples: includeExamples
          ? [
              {
                task: "Check fleet health",
                tool: "fleet_health",
                description: "Aggregates eSIM status counts and low-balance accounts",
              },
              {
                task: "Diagnose offline subscriber",
                tool: "diagnose_subscriber",
                args: { iccid: "8944..." },
                description: "Chains multiple API calls to analyze connectivity issues",
              },
              {
                task: "Check billing status",
                tool: "credit_balance",
                description: "View current credits, overages, and volume discounts",
              },
              {
                task: "Browse available services",
                tool: "service_catalog",
                description: "Discover Carrier ecosystem services and their requirements",
              },
            ]
          : [],
      };

      if (format === "json") {
        return context;
      }

      if (format === "yaml") {
        return { format: "yaml", content: jsonToYaml(context) };
      }

      // Markdown format
      return {
        format: "markdown",
        content: generateMarkdownContext(context),
      };
    },
  },

  // -------------------------------------------------------------------------
  // environment_info — View environment configuration
  // -------------------------------------------------------------------------
  {
    name: "environment_info",
    description:
      "View your Carrier environment configuration — active organization, " +
      "reseller details, connected services, and deployment environment. " +
      "Similar to `stripe projects status`.",
    scope: "read",
    inputSchema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async (env, props) => {
      const sub = props.sub;
      const orgId = props.org_id;

      // The only tool in this file that can still answer without the binding:
      // almost everything it reports comes off the session. So it degrades
      // instead of refusing — but the two flags it genuinely cannot read come
      // back as null, not false. `stripe_billing: false` is a claim about the
      // user's account ("you have no billing set up"); the truth is "this
      // process cannot see whether you do", and those are not the same answer.
      const hasStore = !!env.CARRIER_USERS;

      let orgDetails: Record<string, unknown> | null = null;
      if (orgId && hasStore) {
        orgDetails = await env.CARRIER_USERS.get(`org:${orgId}`, "json").catch(() => null) as Record<string, unknown> | null;
      }

      // Check connected services
      const hasStripe = hasStore
        ? !!(await env.CARRIER_USERS.get(`stripe_customer_id:${sub}`))
        : null;
      const hasWebhook = hasStore
        ? !!(await env.CARRIER_USERS.get(`webhook_url:${sub}`))
        : null;

      return {
        ...(hasStore
          ? {}
          : {
              note:
                "Running as a local stdio server. Fields reported as null are held by " +
                "the hosted Carrier service and cannot be read from here; connect to " +
                "https://mcp.carrier.llc/mcp to see them. Everything else below is " +
                "this session's own configuration and is accurate.",
            }),
        environment: {
          user_sub: sub,
          org_id: orgId ?? null,
          org_name: orgDetails?.name ?? null,
          org_slug: orgDetails?.slug ?? null,
          reseller_id: props.reseller_id,
          reseller_name: props.reseller_name,
          tier: props.tier,
          scopes: props.scope,
          auth_method: props.auth_method ?? "oauth",
        },
        connected_services: {
          esimvault: true, // Always connected (required for operation)
          stripe_billing: hasStripe,
          webhooks: hasWebhook,
          carrier_console: true,
          carrier_api: props.tier !== "free",
        },
        endpoints: {
          mcp: "https://mcp.carrier.llc/mcp",
          api: "https://api.carrier.llc/v1",
          console: "https://app.carrier.llc",
          billing_portal: "https://accounts.carrier.llc/user/billing",
        },
        configuration: {
          ocs_base_url: "https://ocs.esimvault.cloud",
          admin_enabled: props.scope.includes("admin"),
          intelligence_enabled: props.tier !== "free",
        },
      };
    },
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateCredentialRecommendations(
  hasToken: boolean,
  tokenAgeDays: number | null,
): string[] {
  const recommendations: string[] = [];

  if (!hasToken) {
    recommendations.push(
      "No eSIMVault token configured. Complete setup at https://app.carrier.llc/onboarding",
    );
  }

  if (tokenAgeDays !== null && tokenAgeDays > 90) {
    recommendations.push(
      `Token is ${tokenAgeDays} days old. Rotate credentials for security (recommended every 90 days).`,
    );
  }

  if (tokenAgeDays !== null && tokenAgeDays > 180) {
    recommendations.push(
      "URGENT: Token is over 180 days old. Immediate rotation strongly recommended.",
    );
  }

  if (recommendations.length === 0) {
    recommendations.push("All credentials are healthy. No action needed.");
  }

  return recommendations;
}

async function encryptToken(token: string, hexKey: string): Promise<string> {
  const keyBytes = hexToBytes(hexKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey(
    "raw",
    keyBytes.buffer as ArrayBuffer,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );
  const encoded = new TextEncoder().encode(token);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    encoded,
  );
  // Format: base64(iv:ciphertext+tag)
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return btoa(String.fromCharCode(...combined));
}

function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes;
}

function jsonToYaml(obj: unknown, indent = 0): string {
  const spaces = "  ".repeat(indent);
  if (obj === null || obj === undefined) return `${spaces}null`;
  if (typeof obj === "string") return `${spaces}${obj}`;
  if (typeof obj === "number" || typeof obj === "boolean") return `${spaces}${obj}`;
  if (Array.isArray(obj)) {
    return obj.map((item) => `${spaces}- ${typeof item === "object" ? "\n" + jsonToYaml(item, indent + 1) : item}`).join("\n");
  }
  if (typeof obj === "object") {
    return Object.entries(obj as Record<string, unknown>)
      .map(([key, val]) => {
        if (typeof val === "object" && val !== null) {
          return `${spaces}${key}:\n${jsonToYaml(val, indent + 1)}`;
        }
        return `${spaces}${key}: ${val}`;
      })
      .join("\n");
  }
  return String(obj);
}

function generateMarkdownContext(context: Record<string, unknown>): string {
  const proj = context.project as Record<string, unknown>;
  const envInfo = context.environment as Record<string, unknown>;
  const caps = context.capabilities as Record<string, unknown>;
  const billing = context.billing as Record<string, unknown>;
  const practices = context.best_practices as string[];

  return `# Carrier MCP — LLM Context

## Project
- **Name:** ${proj.name}
- **Description:** ${proj.description}
- **Version:** ${proj.version}
- **Transport:** ${proj.transport}
- **Endpoint:** ${proj.endpoint}

## Environment
- **Tier:** ${envInfo.tier}
- **Scopes:** ${(envInfo.scopes as string[]).join(", ")}
- **Reseller:** ${envInfo.reseller_name} (ID: ${envInfo.reseller_id})
- **Auth:** ${envInfo.auth_method}

## Capabilities
- Total tools: ${caps.total_tools}
- Read: ${caps.read_tools} | Write: ${caps.write_tools} | Admin: ${caps.admin_tools}
- Intelligence: ${caps.intelligence_tools} | Pricing: ${caps.pricing_tools} | Projects: ${caps.projects_tools}
- Prompts: ${caps.prompts}

## Billing
- Monthly credits: ${billing.credits_monthly}
- Credit costs: read=1, write=2, admin=5, intelligence=3
- Overages: ${billing.overages_available ? "enabled" : "disabled"}
- Volume discounts: ${billing.volume_discounts_available ? "available" : "not available"}

## Best Practices
${practices.map((p) => `- ${p}`).join("\n")}
`;
}
